import React from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Sparkles,
  Handshake,
  Users,
  FileSearch,
  Filter,
  TrendingUp,
} from "lucide-react";

const services = [
  {
    num: "01",
    slug: "b2b-demand-generation",
    icon: Handshake,
    tint: "#0FB5A6",
    tintBg: "rgba(15,181,166,0.14)",
    tintSoft: "rgba(15,181,166,0.07)",
    title: "B2B Demand Generation",
    desc: "Pipeline that doesn't just fill — it qualifies better and closes faster.",
    proof: "+98% avg. pipeline lift",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=900&auto=format&fit=crop",
  },
  {
    num: "02",
    slug: "b2b-nurture-marketing",
    icon: Users,
    tint: "#FFB000",
    tintBg: "rgba(255,176,0,0.16)",
    tintSoft: "rgba(255,176,0,0.08)",
    title: "B2B Nurture Marketing",
    desc: "Only 10–20% of leads convert fast. We help you win the rest with targeted nurture.",
    proof: "3.4× MQL → SQL velocity",
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=900&auto=format&fit=crop",
  },
  {
    num: "03",
    slug: "b2b-content-marketing",
    icon: FileSearch,
    tint: "#3BA7D1",
    tintBg: "rgba(59,167,209,0.14)",
    tintSoft: "rgba(59,167,209,0.07)",
    title: "B2B Content Marketing",
    desc: "They choose before you pitch. Own the mid-funnel with content that clicks.",
    proof: "+300% organic traffic",
    image:
      "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=900&auto=format&fit=crop",
  },
  {
    num: "04",
    slug: "abm-cx-kaboom",
    icon: Filter,
    tint: "#E96A7F",
    tintBg: "rgba(233,106,127,0.14)",
    tintSoft: "rgba(233,106,127,0.07)",
    title: "ABM & CX",
    desc: "Be precise. Be predictive. Personalize journeys that open doors to key accounts.",
    proof: "22 enterprise deals · $8.4M",
    image:
      "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=900&auto=format&fit=crop",
  },
  {
    num: "05",
    slug: "ai-powered-marketing",
    icon: Sparkles,
    tint: "#FFB000",
    tintBg: "rgba(255,176,0,0.22)",
    tintSoft: "rgba(255,176,0,0.12)",
    title: "AI-Powered Marketing",
    desc: "Proprietary AI workflows for discovery, segmentation, scoring, and personalised outreach.",
    proof: "AI-native, full-funnel",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=900&auto=format&fit=crop",
    featured: true,
  },
];

export default function ServicesGrid() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-section overflow-hidden bg-[#0A0A0A]"
    >
      {/* Dark section with colorful aurora */}
      <div className="absolute inset-0 opacity-45">
        <div className="absolute top-0 left-1/4 w-[680px] h-[680px] rounded-full bg-[radial-gradient(circle,rgba(255,176,0,0.36),transparent_60%)] blur-3xl float-y-slow" />
        <div className="absolute bottom-0 right-1/5 w-[640px] h-[640px] rounded-full bg-[radial-gradient(circle,rgba(59,167,209,0.32),transparent_60%)] blur-3xl float-y" />
        <div className="absolute top-1/3 left-0 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(233,106,127,0.28),transparent_60%)] blur-3xl" />
      </div>
      <div className="grain-overlay" />

      <div className="container-xl relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#FFB000] mb-5">
              <span className="h-1 w-8 bg-[#FFB000]" />
              Services
            </div>
            <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-semibold max-w-3xl">
              Full-funnel B2B marketing,{" "}
              <span className="font-serif italic font-light gradient-text-vivid">
                end to end.
              </span>
            </h2>
            <p className="mt-5 text-white/65 text-base md:text-lg max-w-2xl leading-relaxed">
              Five practices. One operating system. Each backed by measurable
              outcomes — not decks.
            </p>
          </div>
          <a
            href="/services"
            data-testid="services-all-link"
            className="btn-pill btn-primary-amber text-sm self-center md:self-end shrink-0"
          >
            See all services
            <ArrowUpRight size={14} strokeWidth={2.2} />
          </a>
        </div>

        {/* Flow line indicator */}
        <div className="hidden lg:flex items-center gap-2 mb-8 px-2">
          <span className="text-[10px] uppercase tracking-[0.24em] text-white/40">
            Funnel flow
          </span>
          <span className="flex-1 h-px bg-gradient-to-r from-[#0FB5A6]/70 via-[#FFB000]/70 via-[#3BA7D1]/70 via-[#E96A7F]/70 to-[#FFB000]/70" />
          <TrendingUp size={14} strokeWidth={2} className="text-white/40" />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.a
                key={s.num}
                href={`/services/${s.slug}`}
                data-testid={`service-card-${s.num}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.06,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`group gradient-ring card-lift relative flex flex-col rounded-[26px] min-h-[440px] overflow-hidden ${
                  s.featured
                    ? "sm:col-span-2 lg:col-span-1 lg:scale-[1.02] lg:-translate-y-2 shadow-[0_40px_80px_-30px_rgba(255,176,0,0.35)]"
                    : ""
                }`}
                style={{
                  background: `linear-gradient(180deg, ${s.tintBg} 0%, ${s.tintSoft} 100%), #FFFFFF`,
                  border: `1px solid ${s.tint}33`,
                }}
              >
                {/* Glow aura */}
                <div
                  className="absolute -top-20 -right-20 h-48 w-48 rounded-full opacity-30 group-hover:opacity-70 transition-opacity duration-700 blur-3xl pointer-events-none"
                  style={{ background: s.tint }}
                />

                {/* Image tile */}
                <div className="relative aspect-[16/11] overflow-hidden rounded-t-[26px]">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1200ms]"
                    loading="lazy"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(180deg, transparent 30%, ${s.tint}40 100%)`,
                    }}
                  />
                  {/* Floating icon */}
                  <div
                    className="absolute bottom-3 left-3 h-11 w-11 rounded-2xl flex items-center justify-center backdrop-blur-md border shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)]"
                    style={{
                      background: "rgba(255,255,255,0.92)",
                      borderColor: "rgba(255,255,255,0.9)",
                      color: s.tint,
                    }}
                  >
                    <Icon size={18} strokeWidth={1.9} />
                  </div>
                  {/* Number badge */}
                  <div
                    className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.24em] font-semibold px-2.5 py-1 rounded-full"
                    style={{
                      background: "rgba(255,255,255,0.92)",
                      color: s.tint,
                    }}
                  >
                    {s.num}
                  </div>
                </div>

                {/* Content */}
                <div className="relative p-6 md:p-7 flex flex-col flex-1">
                  <h3 className="font-display text-xl md:text-[22px] tracking-tight leading-tight font-medium text-[#1D1D1F] mb-3">
                    {s.title}
                  </h3>
                  <p className="text-[#515154] text-[14px] leading-relaxed mb-5">
                    {s.desc}
                  </p>

                  {/* Proof chip */}
                  <div
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-full self-start mb-5"
                    style={{
                      background: "#FFFFFF",
                      border: `1px solid ${s.tint}55`,
                      color: s.tint,
                    }}
                  >
                    <TrendingUp size={12} strokeWidth={2.2} />
                    {s.proof}
                  </div>

                  <div className="mt-auto pt-5 border-t border-black/5 flex items-center justify-between">
                    <span className="text-sm font-medium text-[#1D1D1F]">
                      Learn more
                    </span>
                    <span
                      className="h-9 w-9 rounded-full flex items-center justify-center transition-all group-hover:rotate-[-6deg] group-hover:scale-110"
                      style={{
                        background: s.tint,
                        color: "#fff",
                        boxShadow: `0 10px 20px -8px ${s.tint}aa`,
                      }}
                    >
                      <ArrowUpRight size={14} strokeWidth={2.4} />
                    </span>
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
